/**
 * Things the user can ask for from more than one place -- the menu bar, a ⋯
 * menu, a toolbar button, a keyboard shortcut. Written once here so every
 * entry point does exactly the same thing and says the same thing afterwards.
 */

import {
  bulkAnalyze,
  bulkArchive,
  bulkDelete,
  engineBase,
  exportPlaylistM3U,
  fetchTrackDetail,
  writeArtwork,
  type ArtworkUpload,
} from "../api";
import { refreshArtwork } from "../shell/Artwork";
import { openTextFile, pickFolder, revealInFileManager, saveTextFile, isDesktop } from "../platform";
import { toast } from "../shell/Toast";
import type { Track } from "../types";
import type { AppState } from "./app";
import { player, type QueueTrack } from "./player";

async function post<T = Record<string, unknown>>(path: string, body: unknown): Promise<T & { error?: string }> {
  const res = await fetch(`${engineBase()}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  return (await res.json()) as T & { error?: string };
}

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

// ---------------------------------------------------------------- library

export async function addFolder(app: AppState) {
  if (app.offline) return toast("The engine is not running, so folders cannot be added yet.", "bad");
  const folder = isDesktop() ? await pickFolder() : window.prompt("Folder to add (full path)")?.trim() || null;
  if (!folder) return;
  toast(`Scanning ${folder}…`);
  const result = await app.library.scan(folder);
  if (result.error) toast(`Not added: ${result.error}`, "bad");
  else toast(`Added ${folder} · ${result.summary}`, "ok");
}

export async function rescanLibrary(app: AppState) {
  const roots = app.library.tree.filter((r) => r.kind === "root" && !r.removed);
  if (!roots.length) return toast("There is no music folder to rescan yet. Add one first.");
  toast(`Rescanning ${plural(roots.length, "folder")}…`);
  for (const root of roots) {
    const error = await app.library.folderAction("rescan", { rootId: root.id });
    if (error) return toast(`Rescan stopped: ${error}`, "bad");
  }
  toast("Library rescanned.", "ok");
}

// ---------------------------------------------------------------- playlists

export async function newPlaylist(app: AppState, tracks: Track[] = []) {
  const name = window.prompt("Playlist name", tracks.length ? tracks[0].album ?? "New playlist" : "New playlist")?.trim();
  if (!name) return;
  const created = await post<{ playlist?: { id: number } }>("/api/playlists/create", { name });
  if (created.error || !created.playlist) return toast(created.error ?? "The playlist was not created.", "bad");
  if (tracks.length) await addToPlaylist(app, created.playlist.id, tracks, name);
  await app.library.refreshPlaylists();
  app.navigate("playlist", { id: String(created.playlist.id) });
}

export async function addToPlaylist(app: AppState, id: number, tracks: Track[], name?: string) {
  const res = await post("/api/playlists/add", { id, items: tracks.map((t) => ({ type: "song", trackId: Number(t.id) })) });
  if (res.error) return toast(res.error, "bad");
  await app.library.refreshPlaylists();
  toast(`Added ${plural(tracks.length, "song")} to ${name ? `“${name}”` : "the playlist"}.`, "ok");
}

export async function exportPlaylist(id: number) {
  const res = await exportPlaylistM3U(id);
  if (res.error || !res.content || !res.fileName) return toast(res.error ?? "Nothing to export.", "bad");
  const where = await saveTextFile(res.fileName, res.content, { name: "M3U playlist", extensions: ["m3u8", "m3u"] });
  if (where) toast(`Saved ${res.fileName} · ${plural(res.songs ?? 0, "song")}.`, "ok");
}

// ---------------------------------------------------------------- flows

export async function exportFlowFile(source: { id: number } | { name: string; spec: unknown }) {
  const res = await post<{ fileName?: string; content?: string }>("/api/flows/file/export", source);
  if (res.error || !res.content || !res.fileName) return toast(res.error ?? "The flow was not exported.", "bad");
  const where = await saveTextFile(res.fileName, res.content, { name: "Nova flow", extensions: ["novaflow"] });
  if (where) toast(`Saved ${res.fileName}. It holds the curves, rules and filters — no songs.`, "ok");
}

export async function importFlowFile(app: AppState) {
  const file = await openTextFile(".novaflow,application/json");
  if (!file) return;
  const res = await post<{ flow?: { id: number; name: string } }>("/api/flows/file/import", { content: file.text });
  if (res.error || !res.flow) return toast(`Could not open ${file.name}: ${res.error ?? "unknown error"}`, "bad");
  toast(`Opened “${res.flow.name}”. Change anything you like; it fills from your library.`, "ok");
  app.navigate("flow", { id: String(res.flow.id) });
}

// ---------------------------------------------------------------- tracks

/** Songs that sound like this one: ranked by match to its energy, tempo and danceability. */
export async function playSimilar(track: Track) {
  const targets: Record<string, number> = {};
  if (track.energyLevel != null) targets.energy = track.energyLevel;
  if (track.bpm != null) targets.bpm = track.bpm;
  if (track.danceability != null) targets.danceability = track.danceability;
  if (!Object.keys(targets).length) return toast("This song has not been analysed yet, so there is nothing to match.", "bad");
  const res = await post<{ candidates?: { trackId: string; title: string | null; artist: string | null; duration: number | null; bpm: number | null; camelot: string | null; lufs: number | null }[] }>(
    "/api/scheduler/candidates",
    { targets, limit: 40 }
  );
  const others = (res.candidates ?? []).filter((c) => c.trackId !== track.id);
  if (!others.length) return toast(res.error ?? "Nothing in the library matches closely enough yet.", "bad");
  const queue: QueueTrack[] = [
    { id: track.id, title: track.title, artist: track.artist, album: track.album, bpm: track.bpm, musicalKey: track.musicalKey, lufs: track.lufs, cueIn: track.cueIn, cueOut: track.cueOut, duration: track.duration },
    ...others.map((c) => ({
      id: c.trackId, title: c.title ?? "Untitled", artist: c.artist ?? "", album: null, bpm: c.bpm,
      musicalKey: c.camelot, lufs: c.lufs, cueIn: null, cueOut: null, duration: c.duration ?? 0,
    })),
  ];
  player.playTracks(queue, 0);
  toast(`Playing “${track.title}” and ${plural(others.length, "song")} like it.`, "ok");
}

export async function showInFolder(track: Track) {
  if (!isDesktop()) return toast("Showing files in their folder needs the desktop app.");
  const detail = await fetchTrackDetail(track.id);
  if (!detail?.path) return toast("The file is not available right now.", "bad");
  await revealInFileManager(detail.path);
}

/**
 * Measure again. Energy and danceability are only redone when asked: the same
 * audio through the same method rates the same, so by default only BPM, key,
 * loudness (and the spectrum, when it is on) are redone.
 */
export async function reanalyse(app: AppState, tracks: Track[], includeDynamics = false) {
  const res = await bulkAnalyze(tracks.map((t) => t.id), false, includeDynamics);
  if (res.error) return toast(res.error, "bad");
  toast(`Re-analysing ${plural(tracks.length, "song")}. Your own edits are kept.`, "ok");
  void app.library.refresh();
}

// ---------------------------------------------------------------- cover art

const COVER_TYPES = ["image/jpeg", "image/png"];
const MAX_COVER_BYTES = 10 * 1024 * 1024;

function pickImage(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = COVER_TYPES.join(",");
    input.style.display = "none";
    input.addEventListener("change", () => {
      resolve(input.files?.[0] ?? null);
      input.remove();
    });
    input.addEventListener("cancel", () => {
      resolve(null);
      input.remove();
    });
    document.body.appendChild(input);
    input.click();
  });
}

function readAsUpload(file: File): Promise<ArtworkUpload> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result);
      resolve({ data: url.slice(url.indexOf(",") + 1), mime: file.type as ArtworkUpload["mime"] });
    };
    reader.onerror = () => reject(reader.error ?? new Error("could not read the image"));
    reader.readAsDataURL(file);
  });
}

/** Write a new front cover into the file (replacing any pictures it had). */
export async function changeCover(track: Track): Promise<boolean> {
  const file = await pickImage();
  if (!file) return false;
  if (!COVER_TYPES.includes(file.type)) return (toast("Choose a JPEG or PNG image.", "bad"), false);
  if (file.size > MAX_COVER_BYTES) return (toast("That image is larger than 10 MB.", "bad"), false);
  const res = await writeArtwork(track.id, await readAsUpload(file));
  if (res.error) return (toast(`Cover not changed: ${res.error}`, "bad"), false);
  refreshArtwork(track.id);
  toast(`New cover written into “${track.title}”. The audio was checked untouched.`, "ok");
  return true;
}

/** Remove every picture from the file. A folder image, if any, shows instead. */
export async function removeCover(track: Track): Promise<boolean> {
  if (!window.confirm(`Remove the cover art from “${track.title}”?\n\nIt is deleted from the file itself.`)) return false;
  const res = await writeArtwork(track.id, null);
  if (res.error) return (toast(`Cover not removed: ${res.error}`, "bad"), false);
  refreshArtwork(track.id);
  toast(`Cover removed from “${track.title}”.`, "ok");
  return true;
}

export async function archive(app: AppState, tracks: Track[]) {
  const res = await bulkArchive(tracks.map((t) => t.id), true);
  if (res.error) return toast(res.error, "bad");
  toast(`Archived ${plural(tracks.length, "song")}. Find them again with archived:yes.`, "ok");
  void app.library.refresh();
}

export async function removeFromLibrary(app: AppState, tracks: Track[]) {
  const what = tracks.length === 1 ? `“${tracks[0].title}”` : plural(tracks.length, "song");
  if (!window.confirm(`Remove ${what} from Nova's library?\n\nThe files on disk are not touched. A rescan adds them back.`)) return;
  const res = await bulkDelete(tracks.map((t) => t.id));
  if (res.error) return toast(res.error, "bad");
  toast(`Removed ${what} from the library.`, "ok");
  void app.library.refresh();
}
